<template>
  <div class="domo-embed-list container-fluid">
    <template v-if="canManageEmbeds">
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <h3 class="h4 mb-0">Domo Embed Management</h3>
          <button
            @click="showCreateModal = true"
            class="btn btn-primary"
          >
            <i class="fa fa-plus-circle me-1"></i> Create Embed
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="row">
      <div class="col text-center py-5">
        <p class="mt-3">Loading embeds...</p>
      </div>
    </div>

    <div v-else-if="error" class="row">
      <div class="col">
        <div class="alert alert-danger" role="alert">
          <i class="fa fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>
      </div>
    </div>

    <div v-else-if="!embeds.length" class="row">
      <div class="col">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fa fa-bar-chart display-4 text-muted mb-3"></i>
            <p class="text-muted">
              No Domo embeds found. Create your first embed to get started.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div class="col">
        <div class="card">
          <div class="card-body p-0">
            <table class="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th>Embed ID</th>
                  <th>Name</th>
                  <th>Active</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th v-if="canManageEmbeds" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="embed in embeds" :key="embed.embed_id">
                  <td><code>{{ embed.embed_id }}</code></td>
                  <td>{{ embed.name }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="embed.active !== false ? 'badge-success' : 'badge-danger'"
                    >
                      {{ embed.active !== false ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>{{ formatDate(embed.created_at) }}</td>
                  <td>{{ formatDate(embed.updated_at) }}</td>
                  <td v-if="canManageEmbeds" class="text-end">
                    <button
                      type="button"
                      @click="editEmbed(embed)"
                      class="btn btn-sm btn-primary me-2"
                      title="Edit"
                      :aria-label="'Edit ' + embed.name"
                    >
                      <i class="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      @click="confirmDelete(embed)"
                      class="btn btn-sm btn-danger"
                      title="Delete"
                      :aria-label="'Delete ' + embed.name"
                    >
                      <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <DomoEmbedForm
      v-if="(showCreateModal || showEditModal) && canManageEmbeds"
      :selected-embed="currentEmbed"
      :is-edit="showEditModal"
      :on-save="saveEmbed"
      @close="closeModal"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-backdrop">
      <div
        class="modal-dialog modal-dialog-centered"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deleteModalTitle"
        tabindex="-1"
      >
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="showDeleteModal = false" aria-label="Close">
              <i class="fa fa-times" aria-hidden="true"></i>
            </button>
            <h5 id="deleteModalTitle" class="modal-title">Confirm Delete</h5>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete the embed "{{ currentEmbed.name }}"?</p>
            <p class="text-muted">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">
              Cancel
            </button>
            <button type="button" class="btn btn-danger" @click="deleteEmbed">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import DomoEmbedForm from './DomoEmbedForm.vue';
import dayjs from 'dayjs';
import { Permissions } from '@/client_auth/permissions';

export default {
  name: 'DomoEmbedList',
  components: {
    DomoEmbedForm,
  },
  data() {
    return {
      embeds: [],
      loading: true,
      error: null,
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      currentEmbed: null,
      canManageEmbeds: false,
    };
  },
  watch: {
    showDeleteModal(val) {
      if (val) {
        document.addEventListener('keydown', this._onDeleteModalKeydown);
      } else {
        document.removeEventListener('keydown', this._onDeleteModalKeydown);
      }
    },
  },
  async mounted() {
    await this.checkPermissions();
    if (this.canManageEmbeds) {
      this.loadEmbeds();
    } else {
      this.loading = false;
    }
  },
  methods: {
    async checkPermissions() {
      const { auth } = this.$angularWorld;
      if (auth && auth.doesUserHavePermission) {
        this.canManageEmbeds = await auth.doesUserHavePermission(Permissions.AnalyticsManager);
      }
    },
    async loadEmbeds() {
      this.loading = true;
      this.error = null;
      try {
        const { domoEmbedsApi } = this.$angularWorld;
        const response = await domoEmbedsApi.listEmbeds();
        this.embeds = response.data || [];
      } catch (err) {
        this.error = 'Failed to load embeds: ' + (err.message || 'Unknown error');
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return dayjs(dateString).format('MMM D, YYYY h:mm A');
    },
    editEmbed(embed) {
      this.currentEmbed = { ...embed };
      this.showEditModal = true;
    },
    confirmDelete(embed) {
      this.currentEmbed = embed;
      this.showDeleteModal = true;
    },
    async deleteEmbed() {
      try {
        const { domoEmbedsApi } = this.$angularWorld;
        await domoEmbedsApi.deleteEmbed(this.currentEmbed.embed_id);
        this.showDeleteModal = false;
        this.currentEmbed = null;
        await this.loadEmbeds();
      } catch (err) {
        this.error = 'Failed to delete embed: ' + (err.message || 'Unknown error');
        this.showDeleteModal = false;
      }
    },
    _onDeleteModalKeydown(e) {
      if (e.key === 'Escape') this.showDeleteModal = false;
    },
    closeModal() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.currentEmbed = null;
    },
    async saveEmbed(embed) {
      try {
        const { domoEmbedsApi } = this.$angularWorld;
        if (this.showEditModal) {
          await domoEmbedsApi.updateEmbed(embed);
        } else {
          await domoEmbedsApi.createEmbed(embed);
        }
        this.closeModal();
        await this.loadEmbeds();
      } catch (err) {
        this.error = 'Failed to save embed: ' + (err.message || 'Unknown error');
        throw err;
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onDeleteModalKeydown);
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
</style>
